import type { FormAnswerInput, FormInstance, FormInstanceItem } from '@/types/formsEngine';
import { contactInputIssue } from './contact-validation';
import {
  addressDraft,
  addressIssue,
  fileUploadAnswerIssue,
  isBlankAddress,
  normalizeAddress,
  timeIssue,
} from './field-types';

export type FormAnswerMap = Record<string, unknown>;

export interface FormRuntimeIssue {
  key: string;
  placementKey: string;
  repeatKey: string;
  label: string;
  message: string;
}

export function currentCalendarDate(now = new Date(), timeZone?: string | null): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      ...(timeZone ? { timeZone } : {}),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (year && month && day) return `${year}-${month}-${day}`;
  } catch {
    // Invalid tenant zones fall back to the browser's local calendar below.
  }
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formDateInputBounds(
  config: FormInstanceItem['config'],
  today = currentCalendarDate(),
): { min?: string; max?: string } {
  return {
    ...(config?.allowPast === false ? { min: today } : {}),
    ...(config?.allowFuture === false ? { max: today } : {}),
  };
}

export function formAnswerKey(placementKey: string, repeatKey = 'default'): string {
  return `${placementKey}::${repeatKey}`;
}

export function buildFormAnswers(
  instance: FormInstance,
  values: FormAnswerMap,
  timestamp = new Date().toISOString(),
): FormAnswerInput[] {
  return instance.sections.flatMap((section) =>
    section.items.flatMap((item) => {
      const key = formAnswerKey(item.placementKey, section.repeatKey);
      return Object.hasOwn(values, key)
        ? [
            {
              placementKey: item.placementKey,
              repeatKey: section.repeatKey,
              value: submittedValue(item, values[key]),
              timestamp,
            },
          ]
        : [];
    }),
  );
}

/** The wire shape of an answer; editing drafts (an address being typed) are tidied here. */
function submittedValue(item: FormInstanceItem, value: unknown): unknown {
  if (item.type === 'ADDRESS') return normalizeAddress(value);
  return value;
}

export function validateFormInstance(
  instance: FormInstance,
  values: FormAnswerMap,
  today = currentCalendarDate(),
): FormRuntimeIssue[] {
  return instance.sections.flatMap((section) =>
    section.items.flatMap((item) => {
      const key = formAnswerKey(item.placementKey, section.repeatKey);
      const message = validateFormItem(
        item,
        Object.hasOwn(values, key) ? values[key] : item.value,
        today,
      );
      return message
        ? [
            {
              key,
              placementKey: item.placementKey,
              repeatKey: section.repeatKey,
              label: item.label,
              message,
            },
          ]
        : [];
    }),
  );
}

/**
 * Whether anything at all is answered, as the server judges a customer's
 * submission: blank is no value, whitespace, an empty list or an address with
 * no street or city; `false` on a checkbox or yes/no is an answer, and so is a
 * photo.
 */
export function hasAnyFormAnswer(instance: FormInstance, values: FormAnswerMap): boolean {
  return instance.sections.some((section) =>
    section.items.some((item) => {
      if (item.photos.length > 0) return true;
      const key = formAnswerKey(item.placementKey, section.repeatKey);
      const value = Object.hasOwn(values, key) ? values[key] : item.value;
      // As the server counts an address: a street or a city, not a country alone.
      if (item.type === 'ADDRESS') {
        const draft = hasFormValue(value) ? addressDraft(value) : null;
        return Boolean(draft?.street.trim() || draft?.city.trim());
      }
      return hasFormValue(value);
    }),
  );
}

export function formProgress(
  instance: FormInstance,
  values: FormAnswerMap,
  today = currentCalendarDate(),
): { completed: number; total: number; percent: number } {
  const items = instance.sections.flatMap((section) =>
    section.items.map((item) => ({
      item,
      value: values[formAnswerKey(item.placementKey, section.repeatKey)] ?? item.value,
    })),
  );
  const completed = items.filter(
    ({ item, value }) => validateFormItem(item, value, today) === null,
  ).length;
  return {
    completed,
    total: items.length,
    percent: items.length === 0 ? 100 : Math.round((completed / items.length) * 100),
  };
}

export function validateFormItem(
  item: FormInstanceItem,
  value: unknown,
  today = currentCalendarDate(),
): string | null {
  const requiredPhotos = item.photoRequired
    ? Math.max(1, item.minPhotos)
    : item.type === 'PHOTO' && item.required
      ? Math.max(1, item.minPhotos)
      : item.minPhotos;
  if (item.photos.length < requiredPhotos) {
    return `${requiredPhotos} photo${requiredPhotos === 1 ? '' : 's'} required`;
  }
  if (item.type === 'PHOTO') return null;
  if (item.type === 'CHECKBOX' && item.required && value !== true) {
    return 'Required';
  }
  if (!hasFormValue(value) || (item.type === 'ADDRESS' && isBlankAddress(value))) {
    return item.required ? 'Required' : null;
  }

  switch (item.type) {
    case 'CHECKBOX':
    case 'YES_NO':
    case 'PASS_FAIL':
      return typeof value === 'boolean' ? null : 'Choose an option';
    case 'TEXT':
      if (typeof value !== 'string') return 'Enter text';
      // A phone answer is stored as +1 and ten digits; its length is the phone
      // rule's to judge, not a character cap set for what the customer types.
      if (
        item.config?.inputType !== 'tel' &&
        item.config?.maxLength !== undefined &&
        value.length > item.config.maxLength
      ) {
        return `Maximum ${item.config.maxLength} characters`;
      }
      return contactInputIssue(item.config?.inputType, value);
    case 'NUMBER':
      if (typeof value !== 'number' || !Number.isFinite(value)) {
        return 'Enter a valid number';
      }
      if (item.config?.min !== undefined && value < item.config.min) {
        return `Minimum ${item.config.min}`;
      }
      if (item.config?.max !== undefined && value > item.config.max) {
        return `Maximum ${item.config.max}`;
      }
      return null;
    case 'DATE':
      if (typeof value !== 'string' || !isCalendarDate(value)) {
        return 'Enter a valid date';
      }
      if (item.config?.allowPast === false && value < today) {
        return 'Past dates are not allowed';
      }
      if (item.config?.allowFuture === false && value > today) {
        return 'Future dates are not allowed';
      }
      return null;
    case 'SINGLE_SELECT':
      return typeof value === 'string' && Boolean(item.config?.options?.includes(value))
        ? null
        : 'Choose a valid option';
    case 'MULTI_SELECT':
      return Array.isArray(value) &&
        new Set(value).size === value.length &&
        value.every(
          (option) => typeof option === 'string' && Boolean(item.config?.options?.includes(option)),
        )
        ? null
        : 'Choose valid options';
    case 'SIGNATURE':
      return isSignature(value) ? null : 'Signature and signer name required';
    case 'BARCODE_SCAN':
      return typeof value === 'string' &&
        value.trim() &&
        matchesBarcodeFormat(value.trim(), item.config?.format)
        ? null
        : `Scan a valid ${item.config?.format ?? 'barcode'} code`;
    case 'MEASUREMENT':
      if (!isMeasurement(value, item.config?.units)) {
        return 'Enter a valid value and unit';
      }
      if (item.config?.min !== undefined && value.value < item.config.min) {
        return `Minimum ${item.config.min}`;
      }
      if (item.config?.max !== undefined && value.value > item.config.max) {
        return `Maximum ${item.config.max}`;
      }
      return null;
    case 'TIME':
      return timeIssue(item.label, value, item.config);
    case 'ADDRESS':
      return addressIssue(item.label, value);
    case 'FILE_UPLOAD':
      return fileUploadAnswerIssue(item.label, value, item.config);
    default:
      return 'Unsupported form item';
  }
}

function hasFormValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'boolean') return true;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

function isSignature(value: unknown): value is { data: string; signerName: string } {
  if (!value || typeof value !== 'object') return false;
  const signature = value as Record<string, unknown>;
  return (
    typeof signature.data === 'string' &&
    signature.data.trim().length > 0 &&
    typeof signature.signerName === 'string' &&
    signature.signerName.trim().length > 0
  );
}

function isMeasurement(
  value: unknown,
  allowedUnits?: string[],
): value is { value: number; unit: string } {
  if (!value || typeof value !== 'object') return false;
  const measurement = value as Record<string, unknown>;
  return (
    typeof measurement.value === 'number' &&
    Number.isFinite(measurement.value) &&
    typeof measurement.unit === 'string' &&
    Boolean(allowedUnits?.includes(measurement.unit))
  );
}

function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}

function matchesBarcodeFormat(
  value: string,
  format: 'UPC' | 'QR' | 'CODE128' | 'ANY' | undefined,
): boolean {
  if (!format || format === 'ANY' || format === 'QR') return true;
  if (format === 'UPC') return /^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(value);
  if (format === 'CODE128') return value.length <= 80 && /^[\x20-\x7E]+$/.test(value);
  return false;
}
